import { Mantra, HawanStep, TarpanaStep } from "./types";

export const VEDIC_MANTRAS: Mantra[] = [
  // ================= VEDIC SANSKRIT =================
  {
    id: "agni_suktam_1",
    title: "Agni Suktam (Rigveda 1.1.1)",
    sanskrit: "अग्निमीळे पुरोहितं यज्ञस्य देवमृत्विजम् । होतारं रत्नधातमम् ॥",
    iast: "agnimīḷe purohitaṃ yajñasya devamṛtvijam | hotāraṃ ratnadhātamam ||",
    translation: "I praise Agni, the chosen priest, God of the sacrifice, the minister, the summoner, who bestows treasure.",
    significance: "This is the very first mantra of the Rigveda, representing the supreme medium of sacrifice (Agni). It is the classic training grounds for Vedic chanting accents.",
    tradition: "vedic",
    tongue: "Sanskrit",
    phoneticSegments: [
      { syllable: "Ag", expectedPitch: "Anudatta", phoneticType: "Kantha (Velar) + Murdha", description: "Vocalized from back of mouth with a lower chest pitch" },
      { syllable: "ni", expectedPitch: "Udatta", phoneticType: "Danta (Dental)", description: "High, clear tone with tip of tongue touching upper teeth" },
      { syllable: "mī", expectedPitch: "Svarita", phoneticType: "Oshtha (Labial)", description: "Circumflex accent, vibrating through the lips with medium pitch" },
      { syllable: "ḷe", expectedPitch: "Anudatta", phoneticType: "Murdha (Retroflex)", description: "Vedic 'ḷa' is retroflex flap, tongue curls completely backward" },
      { syllable: "pu", expectedPitch: "Anudatta", phoneticType: "Oshtha (Labial)", description: "Deep, rounded labial vowel sound" },
      { syllable: "ro", expectedPitch: "Udatta", phoneticType: "Kantha + Oshtha", description: "Vibrant high throat-resonance" },
      { syllable: "hi", expectedPitch: "Svarita", phoneticType: "Kantha (Velar)", description: "Aspirated breath accent at medium pitch" },
      { syllable: "taṃ", expectedPitch: "Anudatta", phoneticType: "Danta + Nasal", description: "Dental stop followed by pure nasal resonance (Anusvara)" }
    ],
    pitchZones: [
      { label: "High", minHz: 180, maxHz: 240, description: "Corresponds to the high-pitched accent. Chanted with upright concentration." },
      { label: "Middle", minHz: 140, maxHz: 179, description: "The circumflex accent. Serves as a descending slide from high to low." },
      { label: "Low", minHz: 100, maxHz: 139, description: "The low-pitched, deep chest accent. Highly grounded." }
    ],
    pronunciationTips: [
      "The letter 'ḷe' (ळ) is a Vedic retroflex lateral consonant, typical of Rigvedic Sanskrit. Ensure your tongue rolls back to the hard palate.",
      "Vibrate 'taṃ' in your nasal cavity (Anusvara) rather than a hard English 'm'.",
      "Keep the transitions between the high pitch (Udatta) and low pitch (Anudatta) smooth and natural."
    ]
  },
  {
    id: "gayatri_mantra",
    title: "Gayatri Mantra (Rigveda 3.62.10)",
    sanskrit: "ॐ भूर्भुवः स्वः । तत्सवितुर्वरेण्यं भर्गो देवस्य धीमहि । धियो यो नः प्रचोदयात् ॥",
    iast: "oṃ bhūr bhuvaḥ svaḥ | tat savitur vareṇyaṃ bhargo devasya dhīmahi | dhiyo yo naḥ pracodayāt ||",
    translation: "We meditate on that most adorable, supreme light of the solar deity; may it inspire and illumine our intellect.",
    significance: "The supreme Vedic prayer for mental illumination. Dedicated to Savitr, the solar energy, which activates our inner spark.",
    tradition: "vedic",
    tongue: "Sanskrit",
    phoneticSegments: [
      { syllable: "OM", expectedPitch: "Svarita", phoneticType: "Pranava (Universal)", description: "Starts in the navel, rises through throat (A), lips (U), and nose (M)" },
      { syllable: "Bhūr", expectedPitch: "Anudatta", phoneticType: "Oshtha + Murdha", description: "Deep, aspirate labial plosive with low-frequency chest vibration" },
      { syllable: "Bhu", expectedPitch: "Anudatta", phoneticType: "Oshtha (Labial)", description: "Short labial vowel, steady and grounded" },
      { syllable: "vaḥ", expectedPitch: "Svarita", phoneticType: "Kantha (Visarga)", description: "Breathy echo of the preceding vowel 'a' ('bhu-vaha')" },
      { syllable: "Svaḥ", expectedPitch: "Udatta", phoneticType: "Danta + Visarga", description: "Aspirated high tone, releasing mental blocks" }
    ],
    pitchZones: [
      { label: "High", minHz: 180, maxHz: 245, description: "Flares up during 'Svaḥ' and 'vi'. Reflects absolute alignment and focus." },
      { label: "Middle", minHz: 140, maxHz: 179, description: "Flows smoothly during 'OM' and 'vaḥ'." },
      { label: "Low", minHz: 100, maxHz: 139, description: "Deep resonance in 'Bhūr' and 'Tat'." }
    ],
    pronunciationTips: [
      "The 'vaḥ' at the end of 'bhuvaḥ' is a Visarga. It should be pronounced with a soft breathing echo: 'bhu-va-ha'.",
      "Ensure 'vareṇyaṃ' is pronounced with the retroflex 'ṇ' (tongue tip touching the roof of the mouth) and not dental 'n'."
    ]
  },

  // ================= AVESTAN / ZOROASTRIAN =================
  {
    id: "atash_niyayesh",
    title: "Atash Niyayesh (Avestan Hymn to Fire)",
    sanskrit: "नेमसे ते आतर्श् मज़्दा अहुराहे हुदा मज़िश्त यज़त ॥",
    iast: "nemase te ātarsh mazdå ahurahe hudå mazishta yazata ||",
    translation: "Homage unto thee, O Fire of Ahura Mazda, who art the beneficent, greatest Yazata (divine spark).",
    significance: "Avestan Fire hymn honoring the sacred light of Ahura Mazda, representing purification, purity, and universal truth.",
    tradition: "avestan",
    tongue: "Avestan",
    phoneticSegments: [
      { syllable: "Ne", expectedPitch: "Anudatta", phoneticType: "Dental Nasal", description: "Humble low starting tone" },
      { syllable: "ma", expectedPitch: "Svarita", phoneticType: "Labial", description: "Balanced nasal transition" },
      { syllable: "se", expectedPitch: "Udatta", phoneticType: "Sibilant", description: "High clear solar pitch" },
      { syllable: "te", expectedPitch: "Anudatta", phoneticType: "Dental Stop", description: "Quiet release of breath" },
      { syllable: "ā", expectedPitch: "Udatta", phoneticType: "Long Vowel", description: "Resonating high opening vowel" },
      { syllable: "tarsh", expectedPitch: "Svarita", phoneticType: "Retroflex", description: "Aspirated sibilant ending" }
    ],
    pitchZones: [
      { label: "High", minHz: 180, maxHz: 235, description: "Peak pitch on 'se' and 'ā'." },
      { label: "Middle", minHz: 140, maxHz: 179, description: "Sliding tone on 'ma' and 'tarsh'." },
      { label: "Low", minHz: 100, maxHz: 139, description: "Grounded base on 'Ne' and 'te'." }
    ],
    pronunciationTips: [
      "Avestan is an ancient sister language to Vedic Sanskrit. Pronounce 'z' as a soft vocalized sibilant, like English 'z' (not found in standard Sanskrit).",
      "Let 'ātarsh' resonate deeply in your chest cavity, representing the sacred ember."
    ]
  },

  // ================= LATIN / GRECO-ROMAN =================
  {
    id: "ignis_sacer",
    title: "Ignis Sacer (Latin Flame Invocation)",
    sanskrit: "Alma Vesta, fove prunam, serva flammam sacratam, fons lucis sempiternae.",
    iast: "Alma Vesta, fove prunam, serva flammam sacratam, fons lucis sempiternae.",
    translation: "Nurturing Vesta, warm the ember, guard the sacred flame, source of eternal light.",
    significance: "Latin hearth prayer invoking the sacred fire of Vesta, the Roman goddess of hearth and family, symbolizing the warmth and continuity of life.",
    tradition: "roman",
    tongue: "Latin",
    phoneticSegments: [
      { syllable: "Al", expectedPitch: "Anudatta", phoneticType: "Alveolar", description: "Grounded, slow lateral sound" },
      { syllable: "ma", expectedPitch: "Svarita", phoneticType: "Labial", description: "Smooth breathing transition" },
      { syllable: "Ves", expectedPitch: "Udatta", phoneticType: "Sibilant", description: "High sibilant spark" },
      { syllable: "ta", expectedPitch: "Anudatta", phoneticType: "Dental Stop", description: "Deep grounded ending vowel" }
    ],
    pitchZones: [
      { label: "High", minHz: 180, maxHz: 240, description: "Flares up during 'Ves' and 'sac'." },
      { label: "Middle", minHz: 140, maxHz: 179, description: "Flows smoothly in 'ma' and 'flu'." },
      { label: "Low", minHz: 100, maxHz: 139, description: "Grounded roots in 'Al' and 'ta'." }
    ],
    pronunciationTips: [
      "Vesta is the keeper of the sacred flame. Pronounce 'Alma' with a warm open 'A' sound.",
      "Chant in a steady, Gregorian-style flat cadence with slow, rhythmic breathing."
    ]
  },

  // ================= ANCIENT NORSE =================
  {
    id: "eldr_galdr",
    title: "Eldr Galdr (Norse Fire Rune Chant)",
    sanskrit: "Heill Eldr, Logi brenni, Thor vigi runar, burt deyi skyggni.",
    iast: "Heill Eldr, Logi brenni, Thor vigi runar, burt deyi skyggni.",
    translation: "Hail Sacred Fire, let the flame burn, may Thor consecrate these runes, let the darkness die away.",
    significance: "An ancient runic fire-consecration galdr (chant) invoking the sparks of Muspelheim and the protection of Thor's hammer.",
    tradition: "norse",
    tongue: "Ancient Norse",
    phoneticSegments: [
      { syllable: "Heill", expectedPitch: "Udatta", phoneticType: "Glottal", description: "High energetic shout of greeting" },
      { syllable: "El", expectedPitch: "Svarita", phoneticType: "Lateral", description: "Vibrant throat resonance" },
      { syllable: "dr", expectedPitch: "Anudatta", phoneticType: "Alveolar trill", description: "Low rolling 'R' trill sound" },
      { syllable: "Lo", expectedPitch: "Svarita", phoneticType: "Vowel Glide", description: "Sliding fire-current" },
      { syllable: "gi", expectedPitch: "Udatta", phoneticType: "Guttural", description: "High lightning-spark release" }
    ],
    pitchZones: [
      { label: "High", minHz: 180, maxHz: 250, description: "Loud, commanding high tones on 'Heill' and 'vigi'." },
      { label: "Middle", minHz: 140, maxHz: 179, description: "Vibrant runic slide on 'El' and 'bren'." },
      { label: "Low", minHz: 100, maxHz: 139, description: "Deep gutteral rolling tones on 'dr' and 'burt'." }
    ],
    pronunciationTips: [
      "Chant 'Heill' with a robust Norse greeting shout to ignite the fireplace.",
      "Roll the 'r' in 'Eldr' and 'runar' with your tongue tip behind your upper teeth to mimic crackling firewood."
    ]
  },
  {
    id: "elder_futhark_aett_1",
    title: "Elder Futhark: Freyr's Aett (ᚠ ᚢ ᚦ ᚨ ᚱ ᚲ ᚷ ᚹ)",
    sanskrit: "ᚠ ᚢ ᚦ ᚨ ᚱ ᚲ ᚷ ᚹ (Fehu, Ūruz, Thurisaz, Ansuz, Raidho, Kenaz, Gebo, Wunjō)",
    iast: "fehu · ūruz · thurisaz · ansuz · raidho · kenaz · gebō · wunjō",
    translation: "Cattle/Wealth, Aurochs/Strength, Giant/Thorn, Sovereign Breath, Journey/Riding, Torch/Ulcer, Gift/Sacrifice, Joy/Kinship.",
    significance: "The primordial first octet of the Elder Futhark representing creative materialization, vitality, divine breath (Ansuz), and cosmic harmony.",
    tradition: "norse",
    tongue: "Proto-Germanic",
    phoneticSegments: [
      { syllable: "Fehu (ᚠ)", expectedPitch: "Udatta", phoneticType: "IPA: /f/ · Labiodental Fricative", description: "Sharp aspirate breath with high front vowel formant (F1: 320 Hz, F2: 2240 Hz)" },
      { syllable: "Ūruz (ᚢ)", expectedPitch: "Anudatta", phoneticType: "IPA: /uː/ · Close Back Vowel", description: "Deep primal guttural rumble of wild aurochs (F1: 340 Hz, F2: 820 Hz)" },
      { syllable: "Thurisaz (ᚦ)", expectedPitch: "Udatta", phoneticType: "IPA: /θ/ · Voiceless Dental Fricative", description: "Penetrating friction spark through front teeth (F1: 450 Hz, F2: 1750 Hz)" },
      { syllable: "Ansuz (ᚨ)", expectedPitch: "Udatta", phoneticType: "IPA: /aː/ · Open Central Vowel", description: "Sovereign breath of Odin/Wodanaz resonating in sinuses (F1: 750 Hz, F2: 1250 Hz)" },
      { syllable: "Raidho (ᚱ)", expectedPitch: "Svarita", phoneticType: "IPA: /r/ · Alveolar Trill", description: "Rolling rhythmic wheel vibration on hard palate (F1: 480 Hz, F2: 1550 Hz)" },
      { syllable: "Kenaz (ᚲ)", expectedPitch: "Udatta", phoneticType: "IPA: /k/ · Velar Plosive", description: "Illuminating spark of the hearth torch (F1: 360 Hz, F2: 2100 Hz)" },
      { syllable: "Gebō (ᚷ)", expectedPitch: "Svarita", phoneticType: "IPA: /g/ · Voiced Velar Fricative", description: "Balanced reciprocity resonance in mid-throat (F1: 520 Hz, F2: 1400 Hz)" },
      { syllable: "Wunjō (ᚹ)", expectedPitch: "Anudatta", phoneticType: "IPA: /w/ · Labial-Velar Approximant", description: "Harmonic communal release returning to deep chest (F1: 380 Hz, F2: 950 Hz)" }
    ],
    pitchZones: [
      { label: "High", minHz: 190, maxHz: 250, description: "Ascendant solar galdr on Fehu (ᚠ), Thurisaz (ᚦ), Ansuz (ᚨ), and Kenaz (ᚲ)." },
      { label: "Middle", minHz: 140, maxHz: 189, description: "Sliding kinetic harmonics on Raidho (ᚱ) and Gebō (ᚷ)." },
      { label: "Low", minHz: 95, maxHz: 139, description: "Primal foundational roots on Ūruz (ᚢ) and Wunjō (ᚹ)." }
    ],
    pronunciationTips: [
      "Vibrate 'Ansuz' by letting nasal air resonate in the sinuses before releasing the terminal sibilant.",
      "Chant 'Fehu' with an explosive opening 'F' followed by deep guttural 'h' aspiration."
    ]
  },
  {
    id: "elder_futhark_aett_2",
    title: "Elder Futhark: Hagal's Aett (ᚺ ᚾ ᛁ ᛃ ᛇ ᛈ ᛉ ᛋ)",
    sanskrit: "ᚺ ᚾ ᛁ ᛃ ᛇ ᛈ ᛉ ᛋ (Hagalaz, Nauthiz, Īsa, Jēra, Eihwaz, Perthro, Algiz, Sōwilō)",
    iast: "hagalaz · nauthiz · īsa · jēra · eihwaz · perthro · algiz · sōwilō",
    translation: "Hail/Disruption, Need/Constraint, Ice/Stillness, Harvest/Year, Yew/Axis, Hearth/Fate, Elk/Sanctuary, Sun/Victory.",
    significance: "The transformative second octet governing elemental disruption, temporal cycles, the cosmic tree axis (Yggdrasil), and solar triumph.",
    tradition: "norse",
    tongue: "Proto-Germanic",
    phoneticSegments: [
      { syllable: "Hagalaz (ᚺ)", expectedPitch: "Udatta", phoneticType: "IPA: /h/ · Voiceless Glottal Fricative", description: "Sudden crack of cosmic hail impacting stone (F1: 420 Hz, F2: 1800 Hz)" },
      { syllable: "Nauthiz (ᚾ)", expectedPitch: "Anudatta", phoneticType: "IPA: /n/ · Alveolar Nasal", description: "Heavy friction of winter survival and necessity (F1: 290 Hz, F2: 1650 Hz)" },
      { syllable: "Īsa (ᛁ)", expectedPitch: "Udatta", phoneticType: "IPA: /iː/ · Close Front Unrounded Vowel", description: "Crystalline static ice needle; unbroken pitch (F1: 270 Hz, F2: 2400 Hz)" },
      { syllable: "Jēra (ᛃ)", expectedPitch: "Svarita", phoneticType: "IPA: /j/ · Palatal Approximant", description: "Cyclical rotation of seasons and solar year (F1: 350 Hz, F2: 2150 Hz)" },
      { syllable: "Eihwaz (ᛇ)", expectedPitch: "Svarita", phoneticType: "IPA: /æː/ · Near-Open Front Vowel", description: "World tree vertical spine connecting realms (F1: 600 Hz, F2: 1720 Hz)" },
      { syllable: "Perthro (ᛈ)", expectedPitch: "Anudatta", phoneticType: "IPA: /p/ · Voiceless Bilabial Plosive", description: "Hidden casting of lot cup in subterranean dark (F1: 400 Hz, F2: 1200 Hz)" },
      { syllable: "Algiz (ᛉ)", expectedPitch: "Udatta", phoneticType: "IPA: /z/ · Voiced Alveolar Fricative", description: "Upturned elk horns channeling divine protection (F1: 380 Hz, F2: 1950 Hz)" },
      { syllable: "Sōwilō (ᛋ)", expectedPitch: "Udatta", phoneticType: "IPA: /s/ · Voiceless Alveolar Sibilant", description: "Ascendant radiant solar wheel illuminating Midgard (F1: 450 Hz, F2: 2300 Hz)" }
    ],
    pitchZones: [
      { label: "High", minHz: 195, maxHz: 255, description: "Sharp crystalline peaks on Hagalaz (ᚺ), Īsa (ᛁ), Algiz (ᛉ), and Sōwilō (ᛋ)." },
      { label: "Middle", minHz: 145, maxHz: 194, description: "Cyclical equilibrium cadences on Jēra (ᛃ) and Eihwaz (ᛇ)." },
      { label: "Low", minHz: 95, maxHz: 144, description: "Dense earthly roots on Nauthiz (ᚾ) and Perthro (ᛈ)." }
    ],
    pronunciationTips: [
      "Keep 'Īsa' as an unbroken flat frequency line, mirroring the absolute immobility of primeval ice.",
      "Sing 'Sōwilō' as an open ascending contour, projecting the solar spark."
    ]
  },
  {
    id: "elder_futhark_aett_3",
    title: "Elder Futhark: Tyr's Aett (ᛏ ᛒ ᛖ ᛗ ᛚ ᛜ ᛞ ᛟ)",
    sanskrit: "ᛏ ᛒ ᛖ ᛗ ᛚ ᛜ ᛞ ᛟ (Tīwaz, Berkano, Ehwaz, Mannaz, Laguz, Ingwaz, Dagaz, Ōthalan)",
    iast: "tīwaz · berkano · ehwaz · mannaz · laguz · ingwaz · dagaz · ōthalan",
    translation: "God Týr/Justice, Birch/Birth, Horse/Synergy, Human/Consciousness, Water/Flow, Ancestor/Seed, Day/Breakthrough, Ancestral Heritage.",
    significance: "The culminating octet of human social order, spiritual initiation, ancestral memory, and transcendent dawn (Dagaz).",
    tradition: "norse",
    tongue: "Proto-Germanic",
    phoneticSegments: [
      { syllable: "Tīwaz (ᛏ)", expectedPitch: "Udatta", phoneticType: "IPA: /t/ · Alveolar Plosive", description: "Spear-point of divine justice and cosmic law (F1: 310 Hz, F2: 2100 Hz)" },
      { syllable: "Berkano (ᛒ)", expectedPitch: "Anudatta", phoneticType: "IPA: /b/ · Bilabial Plosive", description: "Birch womb stirring beneath spring thaw (F1: 420 Hz, F2: 1100 Hz)" },
      { syllable: "Ehwaz (ᛖ)", expectedPitch: "Svarita", phoneticType: "IPA: /eː/ · Close-Mid Front Vowel", description: "Harmonious synergy between rider and steed (F1: 400 Hz, F2: 2000 Hz)" },
      { syllable: "Mannaz (ᛗ)", expectedPitch: "Svarita", phoneticType: "IPA: /m/ · Bilabial Nasal", description: "Awakened intellect and communal selfhood (F1: 300 Hz, F2: 1300 Hz)" },
      { syllable: "Laguz (ᛚ)", expectedPitch: "Anudatta", phoneticType: "IPA: /l/ · Alveolar Lateral Approximant", description: "Fluid surge of primeval ocean and intuition (F1: 390 Hz, F2: 1250 Hz)" },
      { syllable: "Ingwaz (ᛜ)", expectedPitch: "Anudatta", phoneticType: "IPA: /ŋ/ · Velar Nasal", description: "Internalized seed of spiritual gestation (F1: 310 Hz, F2: 2200 Hz)" },
      { syllable: "Dagaz (ᛞ)", expectedPitch: "Udatta", phoneticType: "IPA: /d/ · Dental Plosive", description: "Piercing flash of daybreak over horizon (F1: 480 Hz, F2: 1700 Hz)" },
      { syllable: "Ōthalan (ᛟ)", expectedPitch: "Anudatta", phoneticType: "IPA: /oː/ · Close-Mid Back Vowel", description: "Enclosed sanctuary of generational inheritance (F1: 450 Hz, F2: 900 Hz)" }
    ],
    pitchZones: [
      { label: "High", minHz: 185, maxHz: 245, description: "Ascendant proclamations on Tīwaz (ᛏ) and Dagaz (ᛞ)." },
      { label: "Middle", minHz: 140, maxHz: 184, description: "Harmonic social flow on Ehwaz (ᛖ) and Mannaz (ᛗ)." },
      { label: "Low", minHz: 95, maxHz: 139, description: "Grounded ancestral earth on Berkano (ᛒ), Laguz (ᛚ), Ingwaz (ᛜ), and Ōthalan (ᛟ)." }
    ],
    pronunciationTips: [
      "Deliver 'Tīwaz' with a sharp release of the tongue tip behind upper teeth.",
      "Resonate 'Dagaz' as a triumphant transition from nocturnal mist to daybreak clarity."
    ]
  },

  // ================= WATER LIBATIONS / TARPANA =================
  {
    id: "deva_tarpana_mantra",
    title: "Deva Tarpana (Vedic Water Offering)",
    sanskrit: "ॐ ब्रह्मादयो देवास्तृप्यन्ताम् । ॐ भूर्भुवः स्वः ओम् सृष्टिकारक देवान् तर्पयामि ॥",
    iast: "oṃ brahmādayo devāstṛpyantām | oṃ bhūr bhuvaḥ svaḥ om sṛṣṭikāraka devān tarpayāmi ||",
    translation: "Om. May Lord Brahma and all the divine cosmic forces (Devas) be satisfied by this water. We offer libations to the celestial guardians of creation.",
    significance: "A sacred Vedic mantra used during Tarpana to offer cool water mixed with barley/flowers to satisfy the cosmic deities, bringing peace to the macrocosm.",
    tradition: "vedic",
    tongue: "Sanskrit",
    phoneticSegments: [
      { syllable: "Om", expectedPitch: "Svarita", phoneticType: "Pranava", description: "Universal resonant hum" },
      { syllable: "Brah", expectedPitch: "Anudatta", phoneticType: "Throat", description: "Grounded starting consonant" },
      { syllable: "mā", expectedPitch: "Udatta", phoneticType: "Vowel", description: "High spiritual expansion" },
      { syllable: "da", expectedPitch: "Anudatta", phoneticType: "Dental", description: "Low transition" },
      { syllable: "yo", expectedPitch: "Svarita", phoneticType: "Vowel", description: "Balanced flow" }
    ],
    pitchZones: [
      { label: "High", minHz: 180, maxHz: 240, description: "Rising stream on 'mā' and 'devā'." },
      { label: "Middle", minHz: 140, maxHz: 179, description: "Flowing pool on 'Om' and 'yo'." },
      { label: "Low", minHz: 100, maxHz: 139, description: "Grounded depth on 'Brah' and 'da'." }
    ],
    pronunciationTips: [
      "Pour water slowly from your fingertips (Deva-Teertha) exactly as you chant the word 'tarpayāmi'.",
      "Let your voice flow like water—gentle, steady, and without abrupt stops."
    ]
  },
  {
    id: "aban_niyayesh_water",
    title: "Aban Niyayesh (Avestan Water Libation)",
    sanskrit: "यज़ै अनाहितम् हरौवतिम् आपम् मज़्दधातम् अशओनिम् ॥",
    iast: "yazai anāhitam harauvatim āpem mazdadhātam ashaonim ||",
    translation: "We worship the sacred waters of Anahita, the pure, the flowing, created by Ahura Mazda, bringing health and life.",
    significance: "Avestan Zoroastrian prayer dedicated to Aban (the Waters) and the divinity Anahita, thanking the cosmos for the gift of life-giving water.",
    tradition: "avestan",
    tongue: "Avestan",
    phoneticSegments: [
      { syllable: "Ya", expectedPitch: "Anudatta", phoneticType: "Palatal", description: "Quiet start at low frequency" },
      { syllable: "zai", expectedPitch: "Svarita", phoneticType: "Diphthong", description: "Resonating medium pitch" },
      { syllable: "a", expectedPitch: "Anudatta", phoneticType: "Short Vowel", description: "Short low connector" },
      { syllable: "nā", expectedPitch: "Udatta", phoneticType: "Long Vowel", description: "High surge of water energy" },
      { syllable: "hi", expectedPitch: "Svarita", phoneticType: "Aspirate", description: "Balanced breath release" },
      { syllable: "tam", expectedPitch: "Anudatta", phoneticType: "Nasal Stop", description: "Low grounding terminal" }
    ],
    pitchZones: [
      { label: "High", minHz: 185, maxHz: 245, description: "Vocal crest on 'nā' and 'āp'." },
      { label: "Middle", minHz: 140, maxHz: 184, description: "Flowing current on 'zai' and 'hi'." },
      { label: "Low", minHz: 100, maxHz: 139, description: "Deep pool on 'Ya' and 'tam'." }
    ],
    pronunciationTips: [
      "Anahita represents the divine cosmic river. Pronounce 'anāhitam' with a flowing, soft 'h' sound.",
      "Synchronize your water offering (pour) with the word 'āpem', representing the vital liquid."
    ]
  },
  {
    id: "libatio_latina",
    title: "Libatio (Latin Water & Wine Offering)",
    sanskrit: "Funde latices, divi manes propitii estote, accipite hanc limpham puram.",
    iast: "Funde latices, divi manes propitii estote, accipite hanc limpham puram.",
    translation: "Pour the liquids, O ancestral spirits (Manes) be propitious, accept this pure water.",
    significance: "A classical Roman libation prayer used to honor family ancestors (Lares and Manes) by pouring fresh water or wine onto the earth.",
    tradition: "roman",
    tongue: "Latin",
    phoneticSegments: [
      { syllable: "Fun", expectedPitch: "Anudatta", phoneticType: "Fricative", description: "Deep chest starting tone" },
      { syllable: "de", expectedPitch: "Svarita", phoneticType: "Short Vowel", description: "Smooth transition" },
      { syllable: "la", expectedPitch: "Udatta", phoneticType: "Open Vowel", description: "High release of fluid" },
      { syllable: "ti", expectedPitch: "Svarita", phoneticType: "Dental", description: "Soft, falling pitch" },
      { syllable: "ces", expectedPitch: "Anudatta", phoneticType: "Sibilant", description: "Quiet low closing whisper" }
    ],
    pitchZones: [
      { label: "High", minHz: 180, maxHz: 235, description: "Peak pitch on 'la' and 'est'." },
      { label: "Middle", minHz: 140, maxHz: 179, description: "Transitions in 'de' and 'pur'." },
      { label: "Low", minHz: 100, maxHz: 139, description: "Base tones in 'Fun' and 'ces'." }
    ],
    pronunciationTips: [
      "Romans valued simple, dignified speech. Recite the words slowly while tilting your vessel.",
      "Ensure 'puram' is chanted with a prolonged, steady low tone."
    ]
  }
];

export const HAWAN_STEPS: HawanStep[] = [
  // ================= VEDIC SANSKRIT =================
  {
    id: "step_1_achamanam",
    stepNumber: 1,
    title: "Achamanam (Water Purification)",
    sanskrit: "ॐ केशवाय नमः । ॐ नारायणाय नमः । ॐ माधवाय नमः ॥",
    iast: "oṃ keśavāya namaḥ | oṃ nārāyaṇāya namaḥ | oṃ mādhavāya namaḥ ||",
    translation: "Salutations to Keshava, Narayana, and Madhava.",
    procedure: "Take a small spoonful of water in your right palm. Recite the first phrase and sip the water. Repeat for the second and third phrases. Wash hands.",
    spiritualMeaning: "Purification of body, speech, and mind. Prepares throat for chanting.",
    category: "preliminary",
    tradition: "vedic"
  },
  {
    id: "step_2_pranayama",
    stepNumber: 2,
    title: "Pranayama (Breath Harmonization)",
    sanskrit: "ॐ भूः ॐ भुवः ॐ स्वः ॐ महः ॐ जनः ॐ तपः ॐ सत्यम् ॥",
    iast: "oṃ bhūḥ oṃ bhuvaḥ oṃ svaḥ oṃ mahaḥ oṃ janaḥ oṃ tapaḥ oṃ satyam ||",
    translation: "Om. The seven planes of consciousness.",
    procedure: "Sit comfortably. Close right nostril, inhale through left. Hold while mentally chanting, then exhale through right.",
    spiritualMeaning: "Harmonizing life force (Prana). Steady breath stabilizes chanting pitch.",
    category: "preliminary",
    tradition: "vedic"
  },
  {
    id: "step_3_pavana_kriya",
    stepNumber: 3,
    title: "Pavana Kriya (Lighting the Fire)",
    sanskrit: "ॐ भूर्भुवः स्वः ओम् अग्नये नमः ॥",
    iast: "oṃ bhūr bhuvaḥ svaḥ om agnaye namaḥ ||",
    translation: "Om. To the sacred fire, we bow.",
    procedure: "Light the fire twig or camphor inside the Kunda while fanning gently.",
    spiritualMeaning: "Agni represents the cosmic fire bringing light and carrying prayers.",
    category: "invocation",
    tradition: "vedic"
  },
  {
    id: "step_4_ajya_aahuti",
    stepNumber: 4,
    title: "Ajya-Aahuti (Ghee Offerings)",
    sanskrit: "अग्निमीळे पुरोहितं यज्ञस्य देवमृत्विजम् ।",
    iast: "agnimīḷe purohitaṃ yajñasya devamṛtvijam",
    translation: "Offerings of cow's ghee to the flaming fire.",
    procedure: "Pour melted ghee into the brightest flame using the Sruva exactly at the word 'Svāhā'. Repeat with selected mantras.",
    spiritualMeaning: "Sacrificing our ego ('Na mama') into the fire of wisdom.",
    category: "offering",
    targetMantraId: "agni_suktam_1",
    tradition: "vedic"
  },
  {
    id: "step_5_purna_aahuti",
    stepNumber: 5,
    title: "Purna-Aahuti (Concluding Peace Chant)",
    sanskrit: "ॐ पूर्णमदः पूर्णमिदं पूर्णात् पूर्णमुदच्यते ।",
    iast: "oṃ pūrṇamadaḥ pūrṇamidaṃ pūrṇāt pūrṇamudacyate",
    translation: "Om. From infinite completeness comes completeness.",
    procedure: "Place the final coconut piece or samidha into the fire. Fold hands and bow down.",
    spiritualMeaning: "Infinite peace remains as we close the spiritual circle.",
    category: "concluding",
    tradition: "vedic"
  },

  // ================= AVESTAN / ZOROASTRIAN =================
  {
    id: "step_1_avestan_cleanse",
    stepNumber: 1,
    title: "Padyab (Ablution & Cleansing)",
    sanskrit: "अशम् उपस्तुतिम् मज़्दधातम् ॥",
    iast: "ashem upastutim mazdadhātam ||",
    translation: "Righteousness is the best good, the pure created by Mazda.",
    procedure: "Wash your face, hands, and feet with fresh cold water while facing east to align with the morning solar light.",
    spiritualMeaning: "Purity of body is a prerequisite for experiencing the spark of Ahura Mazda.",
    category: "preliminary",
    tradition: "avestan"
  },
  {
    id: "step_3_ignite_atar",
    stepNumber: 3,
    title: "Igniting the Atar (The Altar Flame)",
    sanskrit: "नेमसे ते आतर्श् मज़्दा ॥",
    iast: "nemase te ātarsh mazdå ||",
    translation: "Homage to the Fire of Mazda.",
    procedure: "Place sandalwood or dry clean twigs into your metal urn and light the flame with a steady match.",
    spiritualMeaning: "Atar represents the son of Ahura Mazda—the cosmic fire of truth and consciousness.",
    category: "invocation",
    targetMantraId: "atash_niyayesh",
    tradition: "avestan"
  },
  {
    id: "step_4_wood_offering",
    stepNumber: 4,
    title: "Aesma-Boidi (Wood and Incense Offering)",
    sanskrit: "उस्ता नो हरौवतिम् आपम् ॥",
    iast: "ustā no harauvatim āpem ||",
    translation: "May light and blessings be ours.",
    procedure: "Offer small dry sandalwood pieces (Aesma) and frankincense into the fire, letting the fragrant smoke rise.",
    spiritualMeaning: "Fragrant wood nourishes the fire and represents clean, pleasant thoughts (Humata).",
    category: "offering",
    tradition: "avestan"
  },

  // ================= ROMAN / LATIN =================
  {
    id: "step_1_roman_cleanse",
    stepNumber: 1,
    title: "Lavatio (Hands Cleansing)",
    sanskrit: "Purifico manus meas limpha de fonte.",
    iast: "Purifico manus meas limpha de fonte.",
    translation: "I purify my hands with water from the spring.",
    procedure: "Dip your hands in cool water, letting it drop to the earth to clear negative physical residuals.",
    spiritualMeaning: "Clears the mundane sphere to step into the sacred domain.",
    category: "preliminary",
    tradition: "roman"
  },
  {
    id: "step_3_hearth_fire",
    stepNumber: 3,
    title: "Igniting the Hearth (Ignis Vesta)",
    sanskrit: "Alma Vesta fove prunam.",
    iast: "Alma Vesta fove prunam.",
    translation: "Nurturing Vesta, warm the ember.",
    procedure: "Light a charcoal or wick on your hearth altar, visualizing the protective light of the home.",
    spiritualMeaning: "The domestic flame connects family lineage directly to the cosmic order.",
    category: "invocation",
    targetMantraId: "ignis_sacer",
    tradition: "roman"
  },
  {
    id: "step_4_roman_grain",
    stepNumber: 4,
    title: "Mola Salsa (Grain and Salt Offering)",
    sanskrit: "Accipe hoc hordeum salitum sacrum.",
    iast: "Accipe hoc hordeum salitum sacrum.",
    translation: "Accept this salted sacred grain.",
    procedure: "Sprinkle spelt flour mixed with sea salt into the hearth flames, watching it crackle.",
    spiritualMeaning: "Mola Salsa represents our daily harvest offered in gratitude to Vesta and the gods.",
    category: "offering",
    tradition: "roman"
  },

  // ================= NORSE =================
  {
    id: "step_1_norse_cleanse",
    stepNumber: 1,
    title: "Vatn (Water Sprinkle)",
    sanskrit: "Hreinsa vatn sál mína.",
    iast: "Hreinsa vatn sál mína.",
    translation: "May the water cleanse my soul.",
    procedure: "Sprinkle cold well water onto your face and hands, channeling the freezing rivers of Niflheim.",
    spiritualMeaning: "Purifies the warrior's spirit before lighting the hearth.",
    category: "preliminary",
    tradition: "norse"
  },
  {
    id: "step_3_norse_fire",
    stepNumber: 3,
    title: "Eldr (Awakening the Fire)",
    sanskrit: "Heill Eldr, Logi brenni.",
    iast: "Heill Eldr, Logi brenni.",
    translation: "Hail Sacred Fire, let the flame burn.",
    procedure: "Strike a flint or light wood blocks to create a robust bonfire or hearth fire.",
    spiritualMeaning: "Eldr is the vital heat of Muspelheim, driving away cold frost giants.",
    category: "invocation",
    targetMantraId: "eldr_galdr",
    tradition: "norse"
  },
  {
    id: "step_4_norse_herb",
    stepNumber: 4,
    title: "Blót-Fórn (Dried Herbs Offering)",
    sanskrit: "Færa furu og einiber til asanna.",
    iast: "Faera furu og einiber til asanna.",
    translation: "Offering pine and juniper to the Aesir.",
    procedure: "Toss dried juniper berries, pine needles, or sage into the roaring fire.",
    spiritualMeaning: "Pine and juniper carry cleansing properties that satisfy Thor and the nature spirits.",
    category: "offering",
    tradition: "norse"
  }
];

export const TARPANA_STEPS: TarpanaStep[] = [
  // ================= VEDIC SANSKRIT =================
  {
    id: "t_step_1_gokarna",
    stepNumber: 1,
    title: "Gokarna Mudra (Hand Vessel)",
    sanskrit: "ॐ अपः पवित्राः कुर्वन्तु ॥",
    iast: "oṃ apaḥ pavitrāḥ kurvantu ||",
    translation: "Om. May the waters purify everything.",
    procedure: "Curl your right palm into the shape of a cow's ear (Gokarna Mudra) to hold water.",
    spiritualMeaning: "The hand becomes a sacred chalice representing receptive water energy.",
    category: "devas",
    tradition: "vedic"
  },
  {
    id: "t_step_2_deva",
    stepNumber: 2,
    title: "Deva Tarpana (Celestial Libations)",
    sanskrit: "ॐ ब्रह्मादयो देवास्तृप्यन्ताम् ॥",
    iast: "oṃ brahmādayo devāstṛpyantām ||",
    translation: "May Brahma and all cosmic devas be satisfied.",
    procedure: "Pour water from the tips of your fingers (Deva-Teertha) into a copper bowl.",
    spiritualMeaning: "Aligning our mind with the macrocosmic order of the deities.",
    category: "devas",
    targetMantraId: "deva_tarpana_mantra",
    tradition: "vedic"
  },
  {
    id: "t_step_3_pitru",
    stepNumber: 3,
    title: "Pitru Tarpana (Ancestral Peace)",
    sanskrit: "ॐ पितृभ्यः स्वधा नमः । तृप्यन्तु मम पितरः ॥",
    iast: "oṃ pitṛbhyaḥ svadhā namaḥ | tṛpyantu mama pitaraḥ ||",
    translation: "Om. To our ancestors, we offer. May my parents and lineages be satisfied.",
    procedure: "Add black sesame seeds (Til) to water. Pour water from the space between your thumb and index finger (Pitru-Teertha) tilting to the right.",
    spiritualMeaning: "Releasing generational debts (Pitru Rina), offering peace to deceased ancestors.",
    category: "ancestors",
    tradition: "vedic"
  },

  // ================= AVESTAN / ZOROASTRIAN =================
  {
    id: "t_step_1_avestan_water",
    stepNumber: 1,
    title: "Vessel Alignment (Sacred Goblet)",
    sanskrit: "आपो मज़्दधाता अशओनिश् ॥",
    iast: "āpo mazdadhātå ashaonish ||",
    translation: "Pure waters created by Mazda.",
    procedure: "Fill a clean silver or copper bowl with fresh cold water, reflecting the morning sky.",
    spiritualMeaning: "Prepares a crystalline mirror to honor the celestial waters.",
    category: "devas",
    tradition: "avestan"
  },
  {
    id: "t_step_2_anahita",
    stepNumber: 2,
    title: "Aban Offering (Honoring Anahita)",
    sanskrit: "यज़ै अनाहितम् हरौवतिम् ॥",
    iast: "yazai anāhitam harauvatim ||",
    translation: "We worship pure Anahita.",
    procedure: "Pour water slowly onto the roots of a nearby living plant or back into a flowing river.",
    spiritualMeaning: "Nourishes the living vegetation, returning Ahura's gift back to nature.",
    category: "devas",
    targetMantraId: "aban_niyayesh_water",
    tradition: "avestan"
  },

  // ================= ROMAN / LATIN =================
  {
    id: "t_step_1_roman_chalice",
    stepNumber: 1,
    title: "Patera Preparation (Ceremonial Bowl)",
    sanskrit: "Accipe hanc pateram limpha plenam.",
    iast: "Accipe hanc pateram limpha plenam.",
    translation: "Receive this patera filled with water.",
    procedure: "Hold the libation bowl (Patera) with both hands, visualizing ancestral protection.",
    spiritualMeaning: "Establishes a bridge between the living home and the spirits of the ancestors.",
    category: "devas",
    tradition: "roman"
  },
  {
    id: "t_step_2_roman_manes",
    stepNumber: 2,
    title: "Parentalia (Honoring Ancestral Manes)",
    sanskrit: "Funde latices divi manes estote.",
    iast: "Funde latices divi manes estote.",
    translation: "Pour the liquids, O divine ancestors.",
    procedure: "Pour the water or wine slowly onto the ground, preferably under an oak tree or near a family tomb.",
    spiritualMeaning: "Feeds the roots of our ancestral tree, ensuring family health and prosperity.",
    category: "ancestors",
    targetMantraId: "libatio_latina",
    tradition: "roman"
  },

  // ================= NORSE =================
  {
    id: "t_step_1_norse_horn",
    stepNumber: 1,
    title: "Horn of Blót (Pouring Horn)",
    sanskrit: "Fylla hornid med hreinu vatni.",
    iast: "Fylla hornid med hreinu vatni.",
    translation: "Fill the horn with pure water.",
    procedure: "Fill a ceremonial drinking horn or wooden cup (Mækir) with spring water or mead.",
    spiritualMeaning: "Water carries the vital blood of Jormungandr and the earth's natural currents.",
    category: "devas",
    tradition: "norse"
  },
  {
    id: "t_step_2_norse_blot",
    stepNumber: 2,
    title: "Blót Libation (Watering the Soil)",
    sanskrit: "Færa vatn til jardar og eldra.",
    iast: "Faera vatn til jardar og eldra.",
    translation: "Offering water to Earth and ancestors.",
    procedure: "Pour water from the horn onto a large sacred stone (Hörgr) or directly onto fertile soil.",
    spiritualMeaning: "Nourishes the land-wights (Landvættir) and honors departed clan spirits in Valhalla.",
    category: "ancestors",
    tradition: "norse"
  }
];
